import type {
    AgentSkillChatEditResponse,
    AgentSkillFlaggedFinding,
    AgentSkillRevision,
} from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { formatTokenDate } from "@helpers/date";
import {
    FLAGGED_FINDING_LABELS,
    KICK_AUTHOR_LABEL,
    KICK_DEFAULT_LABEL,
    PREVIOUS_EDIT_LABEL,
} from "./agentSkillCopy";

export type AgentSkillPanelTab = "chat" | "details" | "versions";

export interface AgentSkillChatItem {
    kind: "system" | "user" | "kick";
    /** Who made the change on a system line; null on chat turns. */
    actor: string | null;
    text: string;
    revisionId: string;
    version: number;
    createdAt: Date;
}

// A revision with no actor was written by Kick: a seeded default or the
// snapshot an override starts from.
export const actorLabel = (revision: AgentSkillRevision) =>
    revision.actor === null
        ? KICK_AUTHOR_LABEL
        : (revision.actor.name ?? revision.actor.email);

// The thread is the revision history read oldest first: a chat revision is a
// user turn and a Kick turn, every other revision is a system line.
export function buildAgentSkillChatThread(
    revisions: AgentSkillRevision[],
): AgentSkillChatItem[] {
    return [...revisions]
        .sort((left, right) => left.version - right.version)
        .flatMap((revision): AgentSkillChatItem[] => {
            const base = {
                actor: null,
                revisionId: revision.id,
                version: revision.version,
                createdAt: revision.createdAt,
            };
            if (revision.chatInstruction !== null) {
                return [
                    {
                        ...base,
                        kind: "user" as const,
                        text: revision.chatInstruction,
                    },
                    {
                        ...base,
                        kind: "kick" as const,
                        text: revision.chatSummary ?? "",
                    },
                ];
            }
            return [
                {
                    ...base,
                    kind: "system" as const,
                    actor: actorLabel(revision),
                    text:
                        revision.version === 1
                            ? "created this skill"
                            : `updated this skill to version ${revision.version}`,
                },
            ];
        });
}

export type RejectedChatEdit = Extract<
    AgentSkillChatEditResponse,
    { outcome: "rejected" }
>;

// A D8 reference error (`unknown_tool:<name>`) shown like a flagged passage,
// so the editor and the chat list both reasons in one shape.
export function consistencyErrorToFinding(
    error: string,
): AgentSkillFlaggedFinding {
    const separator = error.indexOf(":");
    return separator === -1
        ? { code: error, excerpt: "" }
        : {
              code: error.slice(0, separator),
              excerpt: error.slice(separator + 1),
          };
}

const describeFinding = (finding: AgentSkillFlaggedFinding): string =>
    `${FLAGGED_FINDING_LABELS[finding.code] ?? finding.code}: ${finding.excerpt}`;

// One line per reason Kick's draft was not saved, in the order the server
// checked them: unknown tools or skills first, then flagged passages.
export function describeRejectedChatEdit(result: RejectedChatEdit): string[] {
    return [
        ...result.errors.map((error) =>
            describeFinding(consistencyErrorToFinding(error)),
        ),
        ...result.findings.map((finding) =>
            describeFinding({ ...finding, excerpt: `"${finding.excerpt}"` }),
        ),
    ];
}

export interface AgentSkillRevisionGroup<
    T extends GroupableRevision = AgentSkillRevision,
> {
    key: string;
    /** Null for the live skill; the reset heading for an archived override. */
    label: string | null;
    revisions: T[];
}

// Reset dates arrive as Dates when parsed and as ISO strings straight off
// the wire; both compare the same way.
type GroupableRevision = Pick<AgentSkillRevision, "skillId" | "version"> & {
    skillArchivedAt: Date | string | null;
    actor: { id: number } | null;
};

const resetTime = (value: Date | string | null): number =>
    value === null ? 0 : new Date(value).getTime();

// An override starts with a copy of the default it forked from, written as
// its version 1 with no actor. The default's own history shows that text.
const isForkSnapshot = (revision: GroupableRevision) =>
    revision.version === 1 && revision.actor === null;

// The skill's own revisions first, then each reset override under its own
// heading, newest reset first (spec 7), then the Kick default an override was
// forked from: every row numbers its versions from 1, so the default's need
// their own heading. The family also holds the firm's live override when the
// default's own page is open; those edits are the override's and are left
// out. Order within a group is the server's.
export function groupAgentSkillRevisions<T extends GroupableRevision>(
    revisions: T[],
    {
        skillId,
        defaultSkillId,
    }: { skillId: string; defaultSkillId: string | null },
): Array<AgentSkillRevisionGroup<T>> {
    const defaultId = defaultSkillId ?? skillId;
    const isShown = (revision: T) =>
        revision.skillId === defaultId || !isForkSnapshot(revision);
    const kickDefault =
        defaultId === skillId
            ? []
            : revisions.filter(
                  (revision) =>
                      revision.skillArchivedAt === null &&
                      revision.skillId === defaultId,
              );
    const live = revisions.filter(
        (revision) =>
            revision.skillArchivedAt === null &&
            revision.skillId === skillId &&
            isShown(revision),
    );
    const archived = new Map<string, T[]>();
    for (const revision of revisions) {
        if (revision.skillArchivedAt !== null && isShown(revision)) {
            archived.set(revision.skillId, [
                ...(archived.get(revision.skillId) ?? []),
                revision,
            ]);
        }
    }
    const resetGroups = [...archived.entries()]
        .map(([archivedSkillId, group]) => ({
            key: archivedSkillId,
            resetAt: group[0].skillArchivedAt,
            revisions: group,
        }))
        .sort(
            (left, right) => resetTime(right.resetAt) - resetTime(left.resetAt),
        )
        .map(({ key, resetAt, revisions: group }) => ({
            key,
            label: `${PREVIOUS_EDIT_LABEL} (reset on ${formatTokenDate(resetAt)})`,
            revisions: group,
        }));
    return [
        ...(live.length === 0
            ? []
            : [{ key: "live", label: null, revisions: live }]),
        ...resetGroups,
        ...(kickDefault.length === 0
            ? []
            : [
                  {
                      key: "kick-default",
                      label: KICK_DEFAULT_LABEL,
                      revisions: kickDefault,
                  },
              ]),
    ];
}
